/**
 * UnirseProde.js — Página de invitación a torneo Prode
 *
 * Flujo:
 * 1. Usuario abre /unirse/CODIGO
 * 2. Si tiene sesión → lo une al torneo y redirige a /prode?id=XXX
 * 3. Si no tiene sesión → muestra info del torneo + botón Registrarse/Entrar
 *    Al registrarse/loguearse, lo redirige automáticamente al torneo
 */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../../supabaseClient';

const UnirseProde = ({ session }) => {
  const { codigo } = useParams();
  const navigate   = useNavigate();

  const [torneo,   setTorneo]   = useState(null);
  const [estado,   setEstado]   = useState('cargando'); // cargando | listo | uniendo | error | yaEsta
  const [msg,      setMsg]      = useState('');
  const [email,    setEmail]    = useState('');
  const [password, setPassword] = useState('');
  const [nick,     setNick]     = useState('');
  const [tab,      setTab]      = useState('login'); // login | registro
  const [loading,  setLoading]  = useState(false);

  // ── Cargar info del torneo ──────────────────────────────────────────────────
  useEffect(() => {
    const cargar = async () => {
      const { data: t } = await supabase
        .from('prode_torneos')
        .select('id, nombre, creador_id')
        .eq('codigo_invitacion', codigo?.toUpperCase())
        .maybeSingle();

      if (!t) { setEstado('error'); return; }
      setTorneo(t);
      setEstado('listo');
    };
    cargar();
  }, [codigo]);

  // ── Si ya tiene sesión, unir directamente ──────────────────────────────────
  useEffect(() => {
    if (!session || !torneo) return;
    const unirAhora = async () => {
      setEstado('uniendo');

      // Obtener nickname
      const { data: perfil } = await supabase
        .from('perfiles').select('nickname').eq('id', session.user.id).maybeSingle();
      const miNick = perfil?.nickname || session.user.email?.split('@')[0] || 'Jugador';

      // Verificar si ya está
      const { count } = await supabase
        .from('prode_participantes').select('*', { count: 'exact', head: true })
        .eq('torneo_id', torneo.id).eq('user_id', session.user.id);

      if (!count || count === 0) {
        await supabase.from('prode_participantes').insert({
          torneo_id: torneo.id,
          user_id: session.user.id,
          nombre_usuario: miNick,
        });
      }
      // Redirigir al torneo
      navigate(`/prode?id=${torneo.id}`);
    };
    unirAhora();
  }, [session, torneo, navigate]);

  // ── Login ──────────────────────────────────────────────────────────────────
  const handleLogin = async () => {
    setLoading(true); setMsg('');
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) { setMsg('Email o contraseña incorrectos'); setLoading(false); }
    // Si ok → el useEffect de session se dispara y une al torneo
  };

  // ── Registro ───────────────────────────────────────────────────────────────
  const handleRegistro = async () => {
    if (!nick.trim()) { setMsg('Escribí un nickname'); return; }
    if (nick.length < 3) { setMsg('Mínimo 3 caracteres'); return; }
    setLoading(true); setMsg('');

    // Verificar nick único
    const { count } = await supabase
      .from('perfiles').select('*', { count: 'exact', head: true })
      .eq('nickname', nick.trim());
    if (count > 0) { setMsg('Ese nickname ya está en uso'); setLoading(false); return; }

    const { error } = await supabase.auth.signUp({ email, password, options: {
      data: { nickname: nick.trim() }
    }});
    if (error) { setMsg(error.message); setLoading(false); return; }

    // Crear perfil manualmente
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      await supabase.from('perfiles').upsert({ id: user.id, nickname: nick.trim() });
    }
    setLoading(false);
    // El useEffect de session se encarga del resto
  };

  // ── Renders ─────────────────────────────────────────────────────────────────
  const estiloInput = {
    width: '100%', padding: '12px 14px', background: '#0a0f19',
    border: '1px solid #1e293b', borderRadius: '8px', color: '#fff',
    fontSize: '0.95rem', outline: 'none', boxSizing: 'border-box',
  };

  if (estado === 'cargando' || estado === 'uniendo') return (
    <div style={{ minHeight: '100vh', background: '#020b18', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '16px', color: 'white' }}>
      <div style={{ width: '36px', height: '36px', border: '3px solid #1e293b', borderTop: '3px solid #f59e0b', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <p style={{ color: '#f59e0b', margin: 0 }}>{estado === 'uniendo' ? `Uniéndote a ${torneo?.nombre}...` : 'Cargando...'}</p>
    </div>
  );

  if (estado === 'error') return (
    <div style={{ minHeight: '100vh', background: '#020b18', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '12px', color: 'white', padding: '20px' }}>
      <span style={{ fontSize: '3rem' }}>❌</span>
      <p style={{ color: '#ef4444', fontWeight: 'bold', fontSize: '1.1rem', margin: 0 }}>Invitación inválida</p>
      <p style={{ color: '#64748b', margin: 0 }}>El código no existe o expiró.</p>
      <button onClick={() => navigate('/')} style={{ marginTop: '8px', padding: '10px 24px', background: '#f59e0b', color: '#000', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer' }}>
        Ir al inicio
      </button>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: '#020b18', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
      <div style={{ background: '#0f172a', border: '1px solid #f59e0b44', borderRadius: '20px', padding: '32px 28px', maxWidth: '420px', width: '100%', boxShadow: '0 0 40px rgba(245,158,11,0.15)' }}>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <span style={{ fontSize: '2.5rem' }}></span>
          <h2 style={{ color: '#f59e0b', margin: '8px 0 4px', fontSize: '1.3rem', fontWeight: '900', letterSpacing: '1px' }}>
            TE INVITARON AL PRODE
          </h2>
          <p style={{ color: '#e2e8f0', fontWeight: 'bold', fontSize: '1rem', margin: '0 0 4px' }}>
            {torneo?.nombre}
          </p>
          <p style={{ color: '#475569', fontSize: '0.8rem', margin: 0 }}>
            Ingresá o registrate para unirte
          </p>
        </div>

        {/* Tabs login/registro */}
        <div style={{ display: 'flex', background: '#080d14', borderRadius: '10px', padding: '4px', marginBottom: '20px', gap: '4px' }}>
          {[{ key: 'login', label: 'Ya tengo cuenta' }, { key: 'registro', label: 'Registrarme' }].map(({ key, label }) => (
            <button key={key} onClick={() => { setTab(key); setMsg(''); }}
              style={{ flex: 1, padding: '8px', border: 'none', borderRadius: '8px', fontWeight: 'bold', fontSize: '0.82rem', cursor: 'pointer', transition: '0.2s',
                background: tab === key ? '#f59e0b' : 'transparent',
                color: tab === key ? '#000' : '#475569',
              }}>
              {label}
            </button>
          ))}
        </div>

        {/* Formulario */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {tab === 'registro' && (
            <input style={estiloInput} placeholder="Nickname (único)" value={nick}
              onChange={e => setNick(e.target.value)} />
          )}
          <input style={estiloInput} placeholder="Email" type="email" value={email}
            onChange={e => setEmail(e.target.value)} />
          <input style={estiloInput} placeholder="Contraseña" type="password" value={password}
            onChange={e => setPassword(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && (tab === 'login' ? handleLogin() : handleRegistro())} />

          {msg && <p style={{ color: '#ef4444', fontSize: '0.82rem', margin: 0, textAlign: 'center' }}>{msg}</p>}

          <button
            onClick={tab === 'login' ? handleLogin : handleRegistro}
            disabled={loading}
            style={{ width: '100%', padding: '13px', background: loading ? '#1e293b' : '#f59e0b', color: loading ? '#475569' : '#000', border: 'none', borderRadius: '10px', fontWeight: '900', fontSize: '1rem', cursor: loading ? 'default' : 'pointer', transition: '0.2s', marginTop: '4px', letterSpacing: '1px' }}>
            {loading ? 'Procesando...' : tab === 'login' ? '→ Entrar y unirme' : '→ Registrarme y unirme'}
          </button>
        </div>

        {/* Logo */}
        <p style={{ textAlign: 'center', color: '#1e293b', fontSize: '0.75rem', marginTop: '20px', marginBottom: 0 }}>
          <span style={{ color: '#00f2ff', fontWeight: 'bold' }}>DT</span>
          <span style={{ color: '#fff', fontWeight: 'bold' }}>XI</span>
          {' '}— Desafío Táctico
        </p>
      </div>
    </div>
  );
};

export default UnirseProde;